import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { computed, inject } from '@angular/core';

import {
  Observable,
  Subject,
  catchError,
  defer,
  filter,
  interval,
  map,
  merge,
  of,
  pipe,
  repeat,
  switchMap,
  tap,
  timeout,
  timer,
} from 'rxjs';

import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';

import {
  STATUS_PAGE_COUNTDOWN_TICK_IN_MS,
  STATUS_PAGE_FAILURES_BEFORE_BACKOFF,
  STATUS_PAGE_HISTORY_INTERVAL_IN_SECONDS,
  STATUS_PAGE_REQUEST_TIMEOUT_IN_MS,
  backoffIntervalInSeconds,
  dataAgeWarningInSeconds,
} from '../constants/status-page.constants';
import { SYSTEM_HEALTH_COPY } from '../constants/system-health.copy';
import { SYSTEM_HEALTH_API_TOKEN } from '../data-access/system-health-api.token';
import { StatusPageConfigService } from '../services/status-page-config.service';
import {
  SystemHealthDetailResponseDto,
  SystemHealthHistoryResponseDto,
  SystemHealthResponseDto,
} from '../types/system-health-api.types';
import { buildBanner } from '../utils/build-banner';
import { buildComponentViews } from '../utils/build-component-views';
import { buildReportLines } from '../utils/build-report-lines';
import { isIssue } from '../utils/is-issue';

import { initialSystemHealthState } from './system-health.state';

const AUTH_ERROR_STATUSES: readonly number[] = [HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden];
const MILLISECONDS_IN_SECOND = 1000;

interface StatusFetchResult {
  response: SystemHealthResponseDto | SystemHealthDetailResponseDto | null;
  isAuthError: boolean;
}

/**
 * Whether the window held more windows than the page carried. `total` is counted before the
 * gateway's hidden-key filter, so it is only comparable with the page size, never with the
 * rendered rows — comparing it with those would call a filtered page truncated.
 */
function isPagePartial(history: SystemHealthHistoryResponseDto): boolean {
  const { total, limit } = history;
  return typeof total === 'number' && typeof limit === 'number' && total > limit;
}

/** A 401/403 means the gateway closed the detail routes after boot — a config problem, not a network failure. */
function isAuthErrorResponse(error: unknown): boolean {
  return error instanceof HttpErrorResponse && AUTH_ERROR_STATUSES.includes(error.status);
}

/**
 * Single source of truth for the page. Polls the API on a schedule that backs
 * off after repeated failures, keeps the last good data when the API is gone
 * (a blank page during an incident is the worst possible behaviour), and
 * derives everything the templates render as signals.
 */
export const SystemHealthStore = signalStore(
  { providedIn: 'root' },
  withState(initialSystemHealthState),

  withComputed((store, config = inject(StatusPageConfigService)) => ({
    isOffline: computed(() => store.consecutiveFailures() > 0),
    /**
     * The page leads with problems: everything else collapses into one summary line.
     * `isIssue` is the same predicate the banner reads, so the two cannot disagree.
     */
    issues: computed(() => store.components().filter(isIssue)),
    totalCount: computed(() => store.components().length),
    secondsToRefresh: computed(() =>
      Math.max(0, Math.ceil((store.nextRefreshAt() - store.now()) / MILLISECONDS_IN_SECOND))
    ),
    reportLines: computed(() =>
      buildReportLines(
        store.history().items,
        store.history().monitoringGaps,
        SYSTEM_HEALTH_COPY.report.monitoringGapLabel
      )
    ),
    /** True when the API answers but the data it serves is old — the backend is serving a cached tier. */
    isDataDelayed: computed(
      () =>
        store.hasLoaded() &&
        store.consecutiveFailures() === 0 &&
        store.generatedAt() > 0 &&
        store.now() - store.generatedAt() >
          dataAgeWarningInSeconds(config.config().refreshIntervalInSeconds) * MILLISECONDS_IN_SECOND
    ),
  })),

  withComputed(store => ({
    availableCount: computed(() => store.totalCount() - store.issues().length),
    banner: computed(() =>
      buildBanner({
        components: store.components(),
        hasLoaded: store.hasLoaded(),
        isOffline: store.consecutiveFailures() > 0,
        hasAuthError: store.hasAuthError(),
        isDataDelayed: store.isDataDelayed(),
      })
    ),
  })),

  withMethods((store, api = inject(SYSTEM_HEALTH_API_TOKEN), config = inject(StatusPageConfigService)) => {
    const registerFailure = (isAuthError: boolean): void =>
      patchState(store, { consecutiveFailures: store.consecutiveFailures() + 1, hasAuthError: isAuthError });

    const nextDelayInMs = (): number => {
      if (!store.hasLoaded() && store.consecutiveFailures() === 0) return 0;

      const refreshIntervalInSeconds = config.config().refreshIntervalInSeconds;
      const isBackingOff = store.consecutiveFailures() >= STATUS_PAGE_FAILURES_BEFORE_BACKOFF;
      const seconds = isBackingOff ? backoffIntervalInSeconds(refreshIntervalInSeconds) : refreshIntervalInSeconds;
      return seconds * MILLISECONDS_IN_SECOND;
    };

    const troubledKeys = new Subject<void>();
    let lastTroubledKeys: string | null = null;

    const applyStatus = ({ response, isAuthError }: StatusFetchResult): void => {
      if (!response) {
        registerFailure(isAuthError);
        return;
      }
      const components = buildComponentViews(response.components);
      patchState(store, {
        components,
        generatedAt: response.generatedAt ?? 0,
        lastFetchedAt: Date.now(),
        consecutiveFailures: 0,
        hasAuthError: false,
        hasLoaded: true,
      });

      const troubled = components
        .filter(isIssue)
        .map(component => component.key)
        .sort()
        .join('|');
      const hasTroubleMoved = lastTroubledKeys !== null && lastTroubledKeys !== troubled;
      lastTroubledKeys = troubled;
      if (hasTroubleMoved) {
        troubledKeys.next();
      }
    };

    const applyHistory = (history: SystemHealthHistoryResponseDto | null): void => {
      if (!history || history.isUnavailable) {
        patchState(store, { hasReport: true, isReportUnavailable: true });
        return;
      }
      patchState(store, {
        hasReport: true,
        isReportUnavailable: false,
        isReportTruncated: isPagePartial(history),
        history: {
          items: Array.isArray(history.items) ? history.items : [],
          monitoringGaps: Array.isArray(history.monitoringGaps) ? history.monitoringGaps : [],
        },
      });
    };

    /**
     * One poll cycle, re-armed by `repeat()` rather than from inside the subscriber:
     * a throw in `applyStatus` counts as a failure and the page keeps polling, where
     * a self-calling handler would have stopped for good and kept saying "all fine".
     */
    const statusCycle = (): Observable<unknown> =>
      defer(() => {
        const delayInMs = nextDelayInMs();
        patchState(store, { nextRefreshAt: Date.now() + delayInMs });
        return timer(delayInMs);
      }).pipe(
        switchMap(() =>
          api.fetchStatus().pipe(
            timeout(STATUS_PAGE_REQUEST_TIMEOUT_IN_MS),
            map((response): StatusFetchResult => ({ response, isAuthError: false })),
            catchError((error: unknown) =>
              of<StatusFetchResult>({ response: null, isAuthError: isAuthErrorResponse(error) })
            )
          )
        ),
        tap(result => applyStatus(result)),
        catchError(() => {
          registerFailure(false);
          return of(null);
        }),
        repeat()
      );

    /**
     * History is a 24-hour window, so it moves far slower than the live rows: the interval
     * is the floor, and a change in which rows are in trouble is the trigger, so the report
     * cannot sit five minutes behind the banner and contradict it. `switchMap` means a slow
     * response is abandoned rather than racing the next one, and a failed fetch flags
     * the section instead of hiding it.
     */
    const historyCycle = (): Observable<unknown> =>
      merge(timer(0, STATUS_PAGE_HISTORY_INTERVAL_IN_SECONDS * MILLISECONDS_IN_SECOND), troubledKeys).pipe(
        switchMap(() =>
          api.fetchHistory().pipe(
            timeout(STATUS_PAGE_REQUEST_TIMEOUT_IN_MS),
            catchError(() => of(null))
          )
        ),
        tap(history => applyHistory(history)),
        catchError(() => {
          patchState(store, { hasReport: true, isReportUnavailable: true });
          return of(null);
        }),
        repeat()
      );

    return {
      startClock: rxMethod<void>(
        pipe(
          switchMap(() => interval(STATUS_PAGE_COUNTDOWN_TICK_IN_MS)),
          tap(() => patchState(store, { now: Date.now() }))
        )
      ),
      startStatusPolling: rxMethod<void>(pipe(switchMap(() => statusCycle()))),
      startHistoryPolling: rxMethod<void>(
        pipe(
          filter(() => config.isDetailView()),
          switchMap(() => historyCycle())
        )
      ),
    };
  }),

  withHooks({
    onInit(store) {
      store.startClock();
      store.startStatusPolling();
      store.startHistoryPolling();
    },
  })
);
